/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Company logic module.
 * Handles company data retrieval and management operations.
 * @module logic/company
 */

import { refreshToken, signOut } from "../../utils/common.js";

/**
 * Fetches a company by its RFC (tax ID).
 * @async
 * @param {string} rfcEmpresa - The company's RFC identifier
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Object|null>} Company data or null if not found/error
 */
export const getCompanyByRFC = async (rfcEmpresa, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}company/rfc/${rfcEmpresa}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getCompanyByRFC(rfcEmpresa, navigate);
    }
    if (!response.ok) {
      return null;
    }
    return data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Fetches a company by RFC for a specific user.
 * @async
 * @param {string} userId - The user's identifier
 * @param {string} rfcEmpresa - The company's RFC identifier
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Object|null>} Company data or null if not found/error
 */
export const getCompanyByRFCForUser = async (userId, rfcEmpresa, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}company/user/${userId}/rfc/${rfcEmpresa}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getCompanyByRFCForUser(userId, rfcEmpresa, navigate);
    }
    if (!response.ok) {
      return null;
    }
    return data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Fetches a company by its unique identifier.
 * @async
 * @param {string} companyId - The company's unique identifier
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Object|null>} Company data or null if not found/error
 */
export const getCompanyById = async (companyId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}company/${companyId}`,
      options,
    );

    const result = await response.json();
    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getCompanyById(companyId, navigate);
    }
    if (!response.ok) {
      return null;
    }
    return result;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

export const getCompaniesNames = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}company/names`,
      options,
    );

    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getCompaniesNames(navigate);
    }
    if (!response.ok || !Array.isArray(data)) {
      return [];
    }
    return data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};
